import { Fragment, type ReactNode } from 'react'
import KatexRenderer from '../../utils/KatexRenderer'
import { preprocessLegacyMarkup } from '../../utils/textFormatter'

const BOLD_TOKEN_PATTERN = /(\*\*[\s\S]+?\*\*)/g
const COMPLETE_BOLD_TOKEN_PATTERN = /^\*\*([\s\S]+)\*\*$/

export interface LearningContentTextProps {
  text: string | null | undefined
  className?: string
}

function MathText({ text }: { text: string }) {
  const processed = preprocessLegacyMarkup(text)
  if (processed === text && !processed.includes('$$')) {
    return <>{text}</>
  }
  return <KatexRenderer text={processed} />
}

/**
 * 생각열기·포인트·필수체크·빌드업처럼 일반 문장과 수식이 섞인 학습 콘텐츠를 표시한다.
 *
 * 문제 렌더러와 동일하게 $$...$$ 및 legacy 수식 태그를 KaTeX로 처리하면서,
 * 콘텐츠 검수 화면에서 사용하던 **굵게**와 줄바꿈도 보존한다.
 */
export function LearningContentText({
  text,
  className,
}: LearningContentTextProps) {
  if (!text) return null

  const parts = text.split(BOLD_TOKEN_PATTERN)

  const content = (
    <>
      {parts.map((part, index) => {
        if (!part) return null

        const boldMatch = COMPLETE_BOLD_TOKEN_PATTERN.exec(part)
        if (boldMatch) {
          return (
            <strong key={index} className="font-bold">
              <MathText text={boldMatch[1]} />
            </strong>
          )
        }

        return (
          <Fragment key={index}>
            <MathText text={part} />
          </Fragment>
        )
      })}
    </>
  )

  return className ? <span className={className}>{content}</span> : content
}

export function renderLearningContentInline(text: string): ReactNode[] {
  if (!text) return ['']
  return [<LearningContentText key="learning-content" text={text} />]
}
